import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAppVersions, createAppVersion, updateAppVersion } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Package, CheckCircle, XCircle, Plus, Edit } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export function AppVersionsScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [formData, setFormData] = useState({
    version: "",
    buildNumber: "",
    platform: "ios",
    forceUpdate: false,
    isActive: true,
    releaseNotes: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["app-versions"],
    queryFn: getAppVersions,
  });

  const versions = data?.data?.data || [];

  const createMutation = useMutation({
    mutationFn: createAppVersion,
    onSuccess: () => {
      queryClient.invalidateQueries(["app-versions"]);
      toast({
        title: "Başarılı",
        description: "Versiyon başarıyla oluşturuldu",
      });
      handleCloseDialog();
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Versiyon oluşturulamadı",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateAppVersion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["app-versions"]);
      toast({
        title: "Başarılı",
        description: "Versiyon başarıyla güncellendi",
      });
      handleCloseDialog();
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Versiyon güncellenemedi",
        variant: "destructive",
      });
    },
  });

  const handleOpenDialog = (version = null) => {
    if (version) {
      setEditingVersion(version);
      setFormData({
        version: version.version,
        buildNumber: version.buildNumber,
        platform: version.platform,
        forceUpdate: version.isForceUpdate || false,
        isActive: version.isActive,
        releaseNotes: version.releaseNotes || "",
      });
    } else {
      setEditingVersion(null);
      setFormData({
        version: "",
        buildNumber: "",
        platform: "ios",
        forceUpdate: false,
        isActive: true,
        releaseNotes: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingVersion(null);
    setFormData({
      version: "",
      buildNumber: "",
      platform: "ios",
      forceUpdate: false,
      isActive: true,
      releaseNotes: "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingVersion) {
      updateMutation.mutate({ id: editingVersion.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <AdminLayout
      title="App Versions"
      description="Uygulama versiyonlarını yönetin"
      loading={isLoading}
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Package className="mr-2 h-5 w-5" />
              Versions ({versions.length})
            </CardTitle>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Versiyon
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz versiyon bulunmuyor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Build</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Force Update</TableHead>
                  <TableHead>Aktif</TableHead>
                  <TableHead>Release Notes</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell className="font-medium">{version.version}</TableCell>
                    <TableCell>{version.buildNumber}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{version.platform.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      {version.isForceUpdate ? (
                        <Badge variant="destructive">Zorunlu</Badge>
                      ) : (
                        <Badge variant="secondary">Opsiyonel</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {version.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm text-gray-600">
                        {version.releaseNotes || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(version)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog for Create/Edit */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingVersion ? "Versiyonu Düzenle" : "Yeni Versiyon Ekle"}
              </DialogTitle>
              <DialogDescription>
                Uygulama versiyonu bilgilerini girin
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  placeholder="1.0.0"
                  value={formData.version}
                  onChange={(e) =>
                    setFormData({ ...formData, version: e.target.value })
                  }
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="buildNumber">Build Number *</Label>
                <Input
                  id="buildNumber"
                  placeholder="1"
                  value={formData.buildNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, buildNumber: e.target.value })
                  }
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="platform">Platform *</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(value) =>
                    setFormData({ ...formData, platform: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ios">iOS</SelectItem>
                    <SelectItem value="android">Android</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="releaseNotes">Release Notes</Label>
                <Textarea
                  id="releaseNotes"
                  placeholder="Bu versiyondaki yenilikler..."
                  value={formData.releaseNotes}
                  onChange={(e) =>
                    setFormData({ ...formData, releaseNotes: e.target.value })
                  }
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="forceUpdate">Zorunlu Güncelleme</Label>
                <Switch
                  id="forceUpdate"
                  checked={formData.forceUpdate}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, forceUpdate: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="isActive">Aktif</Label>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                {createMutation.isLoading || updateMutation.isLoading
                  ? "Kaydediliyor..."
                  : editingVersion
                  ? "Güncelle"
                  : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

